import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MenuItem,
  makeStyles,
  MenuItemLink,
  mergeClasses,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider
} from "@fluentui/react-components";
import { MoreVertical24Filled } from "@fluentui/react-icons";

const useClasses = makeStyles({
  menuListItem: {
    minWidth: "200px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    },
    "& > * ": {
      color: "#fff"
    },
    "&:hover > * ": {
      color: "inherit!important"
    },
    maxWidth: "unset"
  },
  menuPopover: {
    top: "-15px!important"
  },
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  overlayDrawer: {
    height: "100%"
  }
});

export function ToolbarMoreButton({ styles, items, children }) {
  const classes = useClasses();

  const [open, setOpen] = useState(false);

  const handleItemClick = item => {
    setOpen(false);

    if (item.onClick && typeof item.onClick === "function") {
      item.onClick();
    }
  };

  return (
    <>
      <Menu positioning="above-end" onOpenChange={(_, data) => setOpen(data.open)} open={open}>
        <MenuTrigger disableButtonEnhancement>
          <MenuButton
            appearance="subtle"
            className={open ? mergeClasses(styles.button, classes.buttonPressed) : styles.button}
            icon={<MoreVertical24Filled />}
          />
        </MenuTrigger>
        <MenuPopover className={classes.menuPopover}>
          <MenuList>
            {items.map((group, groupIndex) => (
              <React.Fragment key={`group-${groupIndex}`}>
                <MenuGroup>
                  <MenuGroupHeader>{group.title}</MenuGroupHeader>
                  {group.items.map(item =>
                    item.href ? (
                      <MenuItemLink
                        className={classes.menuListItem}
                        key={item.id || `menu-item-${item.label}`}
                        icon={item.icon}
                        onClick={() => handleItemClick(item)}
                        href={item.href}
                        target={item.target || "_self"}
                      >
                        {item.label}
                      </MenuItemLink>
                    ) : item.submenu ? (
                      <Menu key={item.id || `submenu-${item.label}`}>
                        <MenuTrigger disableButtonEnhancement>
                          <MenuItem className={classes.menuListItem} icon={item.icon}>
                            {item.label}
                          </MenuItem>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            {item.submenu.map(subItem =>
                              subItem.href ? (
                                <MenuItemLink
                                  className={classes.menuListItem}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={() => handleItemClick(subItem)}
                                  href={subItem.href}
                                  target={subItem.target || "_self"}
                                >
                                  {subItem.label}
                                </MenuItemLink>
                              ) : (
                                <MenuItem
                                  className={mergeClasses(classes.menuListItem, item.className || undefined)}
                                  key={subItem.id || `submenu-item-${subItem.label}`}
                                  icon={subItem.icon}
                                  onClick={() => handleItemClick(subItem)}
                                  disabled={subItem.disabled}
                                >
                                  {subItem.label}
                                </MenuItem>
                              )
                            )}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    ) : (
                      <MenuItem
                        className={mergeClasses(classes.menuListItem, item.className || undefined)}
                        key={item.id || `menu-item-${item.label}`}
                        icon={item.icon}
                        onClick={() => handleItemClick(item)}
                        disabled={item.disabled}
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </MenuGroup>
                {groupIndex < items.length - 1 && <MenuDivider key={`divider-${groupIndex}`} />}
              </React.Fragment>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      {children}
    </>
  );
}

// Update propTypes as necessary
ToolbarMoreButton.propTypes = {
  styles: PropTypes.object,
  items: PropTypes.array,
  children: PropTypes.node
};
